import { useState, useEffect, ReactNode } from 'react';
import { Upload, Music2, Image as ImageIcon, Sparkles, ChevronDown, Loader2, FileText, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { MUSIC_TRACK_KIND, generateRandomGradient, type MusicTrack } from '@/lib/musicTypes';
import { Badge } from '@/components/ui/badge';
import { GenreSelector } from '@/components/GenreSelector';
import { LightningZapSplits, type ZapSplit } from '@/components/LightningZapSplits';
import { TermsDialog } from '@/components/TermsDialog';

interface AudioMetadata {
  duration?: number;
  format?: string;
  bitrate?: string;
  sampleRate?: string;
}

interface UploadTrackDialogProps {
  editTrack?: MusicTrack;
  children?: ReactNode;
  onSuccess?: () => void;
}

export function UploadTrackDialog({ editTrack, children, onSuccess }: UploadTrackDialogProps = {}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [gradient, setGradient] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Advanced fields
  const [artist, setArtist] = useState('');
  const [artistNpub, setArtistNpub] = useState('');
  const [album, setAlbum] = useState('');
  const [trackNumber, setTrackNumber] = useState('');
  const [releaseDate, setReleaseDate] = useState(() => {
    // Set default to today's date in YYYY-MM-DD format
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [language, setLanguage] = useState('');
  const [isExplicit, setIsExplicit] = useState(false);
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  const [lyrics, setLyrics] = useState('');
  const [credits, setCredits] = useState('');

  // Zap splits - start with single recipient (empty = use user's npub)
  const [zapSplits, setZapSplits] = useState<ZapSplit[]>([{ npub: '', weight: '1' }]);

  // Audio metadata (auto-detected)
  const [audioMetadata, setAudioMetadata] = useState<AudioMetadata>({});

  // Terms acceptance
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { user } = useCurrentUser();
  const defaultLightningAddress = user?.metadata?.lud16 || user?.metadata?.lud06 || '';
  const { nostr } = useNostr();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Decode artist npub to get pubkey for profile lookup
  let artistPubkey = '';
  try {
    if (artistNpub && artistNpub.startsWith('npub1')) {
      const decoded = nip19.decode(artistNpub);
      if (decoded.type === 'npub') {
        artistPubkey = decoded.data;
      }
    }
  } catch {
    // Invalid npub, ignore
  }

  // Fetch artist profile
  const artistProfile = useAuthor(artistPubkey);

  // Auto-populate artist name from profile when artistNpub changes
  useEffect(() => {
    if (artistProfile.data?.metadata?.name && !artist) {
      setArtist(artistProfile.data.metadata.name);
    }
  }, [artistProfile.data?.metadata?.name, artist]);

  // Pre-fill form when editing
  useEffect(() => {
    if (editTrack && open) {
      // SECURITY: Prevent editing tracks created by other users
      if (editTrack.pubkey !== user?.pubkey) {
        console.error("UploadTrackDialog: Attempted to edit track by different author");
        toast({
          title: 'Cannot edit this track',
          description: 'You can only edit tracks you created.',
          variant: 'destructive',
        });
        setOpen(false);
        return;
      }

      console.log("UploadTrackDialog: Pre-filling form with editTrack:", editTrack);
      console.log("UploadTrackDialog: editTrack.id =", editTrack.id);
      setTitle(editTrack.title);
      setImageUrl(editTrack.imageUrl || '');
      setGradient(editTrack.gradient || []);
      setSelectedGenres(editTrack.genres);
      setIsAiGenerated(editTrack.isAiGenerated);

      // Extract optional metadata from tags
      const tags = editTrack.event.tags;

      // Parse lyrics from tag
      const lyricsTag = tags.find(([name]) => name === 'lyrics')?.[1];
      if (lyricsTag) setLyrics(lyricsTag);

      // Parse credits from content field
      const content = editTrack.event.content;
      if (content) {
        const creditsMatch = content.match(/Credits:\n([\s\S]*?)$/);
        if (creditsMatch) setCredits(creditsMatch[1].trim());
      }
      const artistTag = tags.find(([name]) => name === 'artist')?.[1];
      const artistPubkeyTag = tags.find(([name]) => name === 'p')?.[1];
      const albumTag = tags.find(([name]) => name === 'album')?.[1];
      const trackNumberTag = tags.find(([name]) => name === 'track_number')?.[1];
      const releasedTag = tags.find(([name]) => name === 'released')?.[1];
      const languageTag = tags.find(([name]) => name === 'language')?.[1];
      const explicitTag = tags.find(([name]) => name === 'explicit')?.[1];
      const zapTag = tags.find(([name]) => name === 'zap')?.[1];
      const durationTag = tags.find(([name]) => name === 'duration')?.[1];
      const formatTag = tags.find(([name]) => name === 'format')?.[1];
      const bitrateTag = tags.find(([name]) => name === 'bitrate')?.[1];

      if (artistTag) setArtist(artistTag);
      if (artistPubkeyTag) {
        // Convert hex pubkey to npub for display
        try {
          const npub = nip19.npubEncode(artistPubkeyTag);
          setArtistNpub(npub);
        } catch {
          // If conversion fails, leave empty
          setArtistNpub('');
        }
      }
      if (albumTag) setAlbum(albumTag);
      if (trackNumberTag) setTrackNumber(trackNumberTag);
      if (releasedTag) setReleaseDate(releasedTag);
      if (languageTag) setLanguage(languageTag);
      if (explicitTag) setIsExplicit(explicitTag === 'true');

      // Parse zap splits from tags - encode pubkeys to npubs
      const zapTags = tags.filter(([name]) => name === 'zap');
      if (zapTags.length > 0) {
        setZapSplits(
          zapTags.map(([, pubkey, , weight]) => {
            try {
              // Encode hex pubkey to npub
              const npub = nip19.npubEncode(pubkey);
              return {
                npub,
                weight: weight || '1',
              };
            } catch {
              // If encoding fails, use empty npub
              return {
                npub: '',
                weight: weight || '1',
              };
            }
          })
        );
      } else {
        // Reset to default if no zap splits in the edited track
        setZapSplits([{ npub: '', weight: '1' }]);
      }

      if (durationTag || formatTag || bitrateTag) {
        setAudioMetadata({
          duration: durationTag ? parseInt(durationTag) : undefined,
          format: formatTag,
          bitrate: bitrateTag,
        });
      }
    }
  }, [editTrack, open]);

  const handleAudioFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAudioFile(file);

      // Auto-populate title from filename if title is empty
      if (!title.trim()) {
        const fileName = file.name;
        // Remove file extension
        const titleFromFile = fileName.replace(/\.[^/.]+$/, '');
        setTitle(titleFromFile);
      }

      // Extract audio metadata
      extractAudioMetadata(file);
    }
  };

  const extractAudioMetadata = (file: File) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);

    audio.addEventListener('loadedmetadata', () => {
      const metadata: AudioMetadata = {
        duration: Math.floor(audio.duration),
        format: file.name.split('.').pop()?.toLowerCase(),
      };

      // Estimate bitrate from file size and duration
      if (audio.duration > 0) {
        const bitrateKbps = Math.floor((file.size * 8) / (audio.duration * 1000));
        metadata.bitrate = `${bitrateKbps}kbps`;
      }

      setAudioMetadata(metadata);
      URL.revokeObjectURL(url);
    });

    audio.src = url;
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageUrl('');
      setGradient([]);
    }
  };

  const handleRandomizeGradient = () => {
    setGradient(generateRandomGradient());
    setImageFile(null);
    setImageUrl('');
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    console.log("UploadTrackDialog: handleSubmit called");

    // When editing, audio file is optional
    if (!title.trim()) {
      toast({
        title: 'Missing required fields',
        description: 'Please provide a title.',
        variant: 'destructive',
      });
      return;
    }

    // When creating new track, audio file is required
    if (!editTrack && !audioFile) {
      toast({
        title: 'Missing required fields',
        description: 'Please provide an audio file.',
        variant: 'destructive',
      });
      return;
    }

    console.log("UploadTrackDialog: Validation passed");
    setIsUploading(true);

    try {
      // Upload audio file if provided (or use existing URL if editing)
      let audioUrl = editTrack?.audioUrl || '';
      if (audioFile) {
        console.log("UploadTrackDialog: Starting audio upload");
        toast({ title: 'Uploading audio...' });
        const [[, uploadedUrl]] = await uploadFile(audioFile);
        audioUrl = uploadedUrl;
        console.log("UploadTrackDialog: Audio uploaded to:", audioUrl);
      }

      // Upload image if provided
      let finalImageUrl = imageUrl;
      if (imageFile) {
        console.log("UploadTrackDialog: Starting image upload");
        toast({ title: 'Uploading artwork...' });
        const [[, imgUrl]] = await uploadFile(imageFile);
        finalImageUrl = imgUrl;
        console.log("UploadTrackDialog: Image uploaded to:", finalImageUrl);
      }

      // Use existing identifier if editing, otherwise generate new one
      const trackId = editTrack
        ? editTrack.id
        : `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

      console.log("UploadTrackDialog: trackId =", trackId);
      console.log("UploadTrackDialog: editTrack =", editTrack);
      console.log("UploadTrackDialog: editTrack?.id =", editTrack?.id);

      // Convert selected genres to tags
      const genreTags = selectedGenres.map((g) => ['t', g.toLowerCase()]);

      // When editing, ensure the new event has a later timestamp than the original
      // This is critical for replaceable events - relays only replace if created_at is newer
      const created_at = editTrack
        ? Math.max(editTrack.event.created_at + 1, Math.floor(Date.now() / 1000))
        : Math.floor(Date.now() / 1000);

      console.log("UploadTrackDialog: Event timestamps:", {
        original: editTrack?.event.created_at,
        new: created_at,
      });

      // Build tags
      const tags: string[][] = [
        ['d', trackId],
        ['title', title.trim()],
        ['url', audioUrl],
        ['t', 'music'], // Always include music tag
        ...genreTags,
        ['alt', `Music track: ${title.trim()} by ${artist.trim() || user?.profile?.name || 'Artist'}`],
      ];

      if (finalImageUrl) {
        tags.push(['image', finalImageUrl]);
      }

      // Add auto-detected metadata
      if (audioMetadata.duration) {
        tags.push(['duration', audioMetadata.duration.toString()]);
      }
      if (audioMetadata.format) {
        tags.push(['format', audioMetadata.format]);
      }
      if (audioMetadata.bitrate) {
        tags.push(['bitrate', audioMetadata.bitrate]);
      }
      if (audioMetadata.sampleRate) {
        tags.push(['sample_rate', audioMetadata.sampleRate]);
      }

      // Add optional advanced fields
      if (artist.trim()) {
        tags.push(['artist', artist.trim()]);
      }
      if (artistNpub.trim()) {
        try {
          // Decode npub to hex pubkey
          const decoded = nip19.decode(artistNpub.trim());
          if (decoded.type === 'npub') {
            tags.push(['p', decoded.data]);
          }
        } catch {
          // Invalid npub, skip adding the tag
          toast({
            title: 'Invalid artist npub',
            description: 'The artist npub you provided is invalid and will be ignored.',
            variant: 'default',
          });
        }
      }
      if (album.trim()) {
        tags.push(['album', album.trim()]);
      }
      if (trackNumber.trim()) {
        tags.push(['track_number', trackNumber.trim()]);
      }
      if (releaseDate.trim()) {
        tags.push(['released', releaseDate.trim()]);
      }
      if (language.trim()) {
        tags.push(['language', language.trim()]);
      }
      if (isExplicit) {
        tags.push(['explicit', 'true']);
      }
      if (isAiGenerated) {
        tags.push(['ai-generated', 'true']);
      }
      if (lyrics.trim()) {
        tags.push(['lyrics', lyrics.trim()]);
      }

      // Add zap splits - decode npubs to pubkeys
      // Filter out empty weights and resolve empty npubs to user's npub
      const zapSplitsToProcess = zapSplits
        .filter((split) => parseInt(split.weight) > 0)
        .map((split) => ({
          npub: split.npub.trim() || user?.npub || '',
          weight: split.weight,
        }))
        .filter((split) => split.npub);

      if (zapSplitsToProcess.length > 0) {
        // Check for duplicate npubs
        const npubs = zapSplitsToProcess.map(s => s.npub.toLowerCase());
        const uniqueNpubs = new Set(npubs);
        if (npubs.length !== uniqueNpubs.size) {
          toast({
            title: 'Duplicate recipients',
            description: 'Each zap split must have a unique npub. Please remove duplicates.',
            variant: 'destructive',
          });
          setIsUploading(false);
          return;
        }

        // Decode npubs to pubkeys
        try {
          for (const split of zapSplitsToProcess) {
            // Decode npub to hex pubkey
            const decoded = nip19.decode(split.npub);
            if (decoded.type !== 'npub') {
              toast({
                title: 'Invalid npub',
                description: `${split.npub} is not a valid npub`,
                variant: 'destructive',
              });
              setIsUploading(false);
              return;
            }

            const pubkey = decoded.data;

            // Add zap tag with pubkey, empty relay hint, and weight
            // Format: ["zap", "<hex-pubkey>", "<relay-url>", "<weight>"]
            tags.push(['zap', pubkey, '', split.weight]);
          }
        } catch (error) {
          console.error('Error processing zap splits:', error);
          toast({
            title: 'Zap split processing failed',
            description: error instanceof Error ? error.message : 'An error occurred',
            variant: 'destructive',
          });
          setIsUploading(false);
          return;
        }
      }

      // Content field for credits if provided
      let content = '';
      if (credits.trim()) {
        content = `Credits:\n${credits.trim()}`;
      }

      // Create the music track event
      console.log("UploadTrackDialog: About to call createEvent with:", {
        kind: MUSIC_TRACK_KIND,
        content: content.trim(),
        tags,
      });
      toast({ title: 'Publishing track...' });

      await createEvent({
        kind: MUSIC_TRACK_KIND,
        content: content.trim(),
        tags,
        created_at,
      });

      console.log("UploadTrackDialog: Event created successfully");

      // Invalidate all relevant queries to refetch updated data
      await queryClient.invalidateQueries({ queryKey: ['music-track'] });
      await queryClient.invalidateQueries({ queryKey: ['music-tracks'] });
      await queryClient.invalidateQueries({ queryKey: ['music-tracks-by-author'] });

      toast({
        title: editTrack ? 'Track updated!' : 'Track uploaded!',
        description: editTrack
          ? 'Your changes have been saved.'
          : 'Your music is now live on Nostr.',
      });
      setOpen(false);
      resetForm();

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("UploadTrackDialog: Error during upload:", error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editTrack) return;

    setShowDeleteDialog(false);
    setIsDeleting(true);

    try {
      // Publish a NIP-09 deletion event (kind 5)
      // This requests relays to delete the track event
      await createEvent({
        kind: 5,
        content: 'Deleted track',
        tags: [
          ['e', editTrack.event.id],
          ['a', `${MUSIC_TRACK_KIND}:${editTrack.pubkey}:${editTrack.id}`],
        ],
      });

      // Invalidate all relevant queries to refetch updated data
      await queryClient.invalidateQueries({ queryKey: ['music-track'] });
      await queryClient.invalidateQueries({ queryKey: ['music-tracks'] });
      await queryClient.invalidateQueries({ queryKey: ['music-tracks-by-author'] });

      toast({
        title: 'Track deleted',
        description: 'Your track has been deleted from relays.',
      });

      setOpen(false);
      resetForm();

      // Navigate to home page after deletion
      navigate('/');

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setAudioFile(null);
    setImageFile(null);
    setImageUrl('');
    setSelectedGenres([]);
    setGradient([]);
    setShowAdvanced(false);
    setArtist('');
    setArtistNpub('');
    setAlbum('');
    setTrackNumber('');
    setReleaseDate('');
    setLanguage('');
    setIsExplicit(false);
    setIsAiGenerated(false);
    setLyrics('');
    setCredits('');
    setZapSplits([{ npub: '', weight: '1' }]);
    setAudioMetadata({});
    setAgreedToTerms(false);
  };

  if (!user) {
    return null;
  }

  const previewStyle = imageFile
    ? { backgroundImage: `url(${URL.createObjectURL(imageFile)})` }
    : imageUrl
    ? { backgroundImage: `url(${imageUrl})` }
    : gradient.length > 0
    ? { background: `linear-gradient(135deg, ${gradient.join(', ')})` }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button size="icon" className="h-10 w-10 rounded-full bg-white hover:bg-white/90 text-black">
            <Upload className="h-5 w-5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
          <DialogHeader>
            <DialogTitle>{editTrack ? 'Edit Track' : 'Upload Music Track'}</DialogTitle>
            <DialogDescription>
              {editTrack
                ? 'Update your track details and settings'
                : 'Share your music with the Nostr community and earn zaps'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6">
          <div className="space-y-4 sm:space-y-6">
          {/* Track Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Track Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summer Nights"
              required
            />
          </div>

          {/* Audio File */}
          <div className="space-y-2">
            <Label htmlFor="audio">
              Audio File {editTrack ? '(optional - leave blank to keep current)' : '*'}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="audio"
                type="file"
                accept="audio/*"
                onChange={handleAudioFileChange}
                className="cursor-pointer"
                required={!editTrack}
              />
              {audioFile && (
                <Badge variant="secondary" className="gap-1">
                  <Music2 className="h-3 w-3" />
                  {audioFile.name}
                </Badge>
              )}
            </div>
          </div>

          {/* Album Art */}
          <div className="space-y-2">
            <Label>Album Art</Label>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Preview - Smaller on mobile, larger on desktop */}
              <div className="flex-shrink-0">
                {previewStyle ? (
                  <div
                    className="w-32 h-32 sm:w-48 sm:h-48 rounded-lg bg-cover bg-center"
                    style={previewStyle}
                  >
                    {!imageFile && !imageUrl && gradient.length > 0 && (
                      <div className="h-full flex items-center justify-center">
                        <Music2 className="h-8 w-8 sm:h-12 sm:w-12 text-white/80" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-32 h-32 sm:w-48 sm:h-48 rounded-lg border border-dashed border-white/[0.11] bg-white/[0.11] flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Controls - Now on the right */}
              <div className="flex-1 space-y-3">
                {/* Upload options */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 gap-2 text-xs sm:text-sm"
                    onClick={() => document.getElementById('image')?.click()}
                  >
                    <ImageIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Upload Image</span>
                    <span className="sm:hidden">Upload</span>
                  </Button>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2 text-xs sm:text-sm"
                    onClick={handleRandomizeGradient}
                  >
                    <Sparkles className="h-4 w-4" />
                    Random
                  </Button>
                </div>

                {/* Or URL */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                <Input
                  placeholder="Paste image URL"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setImageFile(null);
                    setGradient([]);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Genres */}
          <div className="space-y-2">
            <Label>Genres</Label>
            <GenreSelector
              selectedGenres={selectedGenres}
              onChange={setSelectedGenres}
            />
            <p className="text-xs text-muted-foreground">
              Add genres to help people discover your music
            </p>
          </div>

          {/* Artist npub */}
          <div className="space-y-2">
            <Label htmlFor="artistNpub">Artist Account (npub)</Label>
            <Input
              id="artistNpub"
              value={artistNpub}
              onChange={(e) => setArtistNpub(e.target.value)}
              placeholder="npub1... (optional)"
            />
            <p className="text-xs text-muted-foreground">
              If the artist has a Nostr account, their profile will be linked
            </p>
          </div>

          {/* Artist Name */}
          <div className="space-y-2">
            <Label htmlFor="artist">Artist Name</Label>
            <Input
              id="artist"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              placeholder="Leave blank to use your profile name"
            />
            {artistProfile.data?.metadata?.name && (
              <p className="text-xs text-muted-foreground">
                Auto-filled from Nostr profile
              </p>
            )}
          </div>

          {/* AI-Generated Toggle */}
          <div className="flex items-start justify-between gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-white/[0.11] bg-muted/30">
            <div className="space-y-0.5 flex-1">
              <Label htmlFor="ai-generated" className="text-sm sm:text-base cursor-pointer">
                AI-Generated
              </Label>
              <p className="text-xs sm:text-sm text-muted-foreground">
                This track was assisted in part or generated in full by AI
              </p>
            </div>
            <div className="flex-shrink-0 pt-0.5">
              <Switch
                id="ai-generated"
                checked={isAiGenerated}
                onCheckedChange={setIsAiGenerated}
              />
            </div>
          </div>

          {/* Lightning Address & Zap Splits */}
          <LightningZapSplits
            zapSplits={zapSplits}
            onZapSplitsChange={setZapSplits}
            artistNpub={artistNpub}
          />

          {/* Advanced Settings */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-between p-3 sm:p-4 h-auto hover:bg-muted/50 hover:text-foreground border border-white/[0.11] text-sm sm:text-base"
              >
                <span className="font-medium text-foreground">Advanced Settings</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    showAdvanced ? 'rotate-180' : ''
                  }`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 sm:space-y-4 pt-3 sm:pt-4">
              {/* Album & Track Number */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor="album">Album</Label>
                  <Input
                    id="album"
                    value={album}
                    onChange={(e) => setAlbum(e.target.value)}
                    placeholder="Album name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trackNumber">Track #</Label>
                  <Input
                    id="trackNumber"
                    type="number"
                    min="1"
                    value={trackNumber}
                    onChange={(e) => setTrackNumber(e.target.value)}
                    placeholder="1"
                  />
                </div>
              </div>

              {/* Release Date */}
              <div className="space-y-2">
                <Label htmlFor="releaseDate">Release Date</Label>
                <Input
                  id="releaseDate"
                  type="date"
                  value={releaseDate}
                  onChange={(e) => setReleaseDate(e.target.value)}
                />
              </div>

              {/* Language & Explicit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Input
                    id="language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    placeholder="en"
                    maxLength={2}
                  />
                  <p className="text-xs text-muted-foreground">ISO 639-1 code</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="explicit">Content Warning</Label>
                  <div className="flex items-center space-x-2 h-10">
                    <input
                      type="checkbox"
                      id="explicit"
                      checked={isExplicit}
                      onChange={(e) => setIsExplicit(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="explicit" className="text-sm">
                      Explicit content
                    </label>
                  </div>
                </div>
              </div>

              {/* Lyrics */}
              <div className="space-y-2">
                <Label htmlFor="lyrics">Lyrics</Label>
                <Textarea
                  id="lyrics"
                  value={lyrics}
                  onChange={(e) => setLyrics(e.target.value)}
                  placeholder="Enter song lyrics..."
                  rows={6}
                  className="font-mono text-sm"
                />
              </div>

              {/* Credits */}
              <div className="space-y-2">
                <Label htmlFor="credits">Credits</Label>
                <Textarea
                  id="credits"
                  value={credits}
                  onChange={(e) => setCredits(e.target.value)}
                  placeholder="Producer: John Doe&#10;Mix Engineer: Jane Smith&#10;Mastering: ..."
                  rows={4}
                />
              </div>

              {/* Auto-detected Metadata */}
              {audioMetadata.duration && (
                <div className="space-y-2 p-3 sm:p-4 bg-muted/30 rounded-lg">
                  <Label className="text-xs text-muted-foreground">
                    Detected Audio Info
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                    {audioMetadata.duration && (
                      <div>
                        <span className="text-muted-foreground">Duration:</span>{' '}
                        {Math.floor(audioMetadata.duration / 60)}:
                        {(audioMetadata.duration % 60).toString().padStart(2, '0')}
                      </div>
                    )}
                    {audioMetadata.format && (
                      <div>
                        <span className="text-muted-foreground">Format:</span>{' '}
                        {audioMetadata.format.toUpperCase()}
                      </div>
                    )}
                    {audioMetadata.bitrate && (
                      <div>
                        <span className="text-muted-foreground">Bitrate:</span>{' '}
                        {audioMetadata.bitrate}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Terms Agreement */}
          <div className="flex items-start space-x-2 sm:space-x-3 p-3 sm:p-4 rounded-lg border border-white/[0.11] bg-muted/30">
            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1">
              <Label
                htmlFor="terms"
                className="text-xs sm:text-sm font-medium leading-none cursor-pointer"
              >
                I agree to the terms of service and verify copyright ownership
              </Label>
              <p className="text-xs sm:text-sm text-muted-foreground">
                By checking this box, you confirm that you own the copyright to this track or have the necessary rights to share it, and you agree to NodeCast's{' '}
                <TermsDialog>
                  <button
                    type="button"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    terms of service
                    <FileText className="h-3 w-3" />
                  </button>
                </TermsDialog>
                .
              </p>
            </div>
          </div>

          {/* Upload Info Memo */}
          <div className="p-3 sm:p-4 bg-sky-500/10 border border-sky-500/30 rounded-lg">
            <p className="text-xs sm:text-sm text-sky-600 dark:text-sky-400">
              <strong className="text-sky-700 dark:text-sky-300">Note:</strong> Please do not close this window while uploading.
              Upload time may vary depending on file size, typically taking up to a minute for larger files.
            </p>
          </div>

          {/* Delete Button - Only show when editing */}
          {editTrack && (
            <div className="pt-4 border-t border-white/[0.11]">
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteClick}
                disabled={isDeleting || isUploading}
                className="w-full gap-2"
              >
                {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isDeleting ? (
                  'Deleting...'
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Track
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                This will request relays to delete your track. This action cannot be undone.
              </p>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Track?</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{editTrack?.title}"? This will broadcast a deletion request to all relays. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Submit */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isUploading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isUploading || !agreedToTerms}
              className="flex-1 gap-2"
            >
              {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isUploading
                ? editTrack
                  ? 'Updating...'
                  : 'Uploading...'
                : editTrack
                ? 'Update Track'
                : 'Publish Track'}
            </Button>
          </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
